<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminSidebar from './AdminSidebar.vue'
import { useAdminAuth } from '@/composables/useAdminAuth'

const { t } = useI18n()
const { superAdmin, logout } = useAdminAuth()
const router = useRouter()

const mobileOpen = ref(false)

async function handleLogout(): Promise<void> {
  await logout()
  await router.push({ name: 'admin.login' })
}
</script>

<template>
  <div class="h-screen overflow-hidden flex bg-slate-950 text-white">
    <AdminSidebar :mobile-open="mobileOpen" @close-mobile="mobileOpen = false" />

    <div class="flex-1 flex flex-col overflow-hidden min-w-0">
      <!-- Slim top bar -->
      <header class="shrink-0 bg-slate-900 border-b border-slate-700 px-4 py-3 flex items-center gap-4">
        <!-- Mobile hamburger (hidden on desktop — sidebar is always visible) -->
        <button
          type="button"
          class="lg:hidden text-slate-400 hover:text-white transition-colors"
          :aria-label="t('admin.nav.open_menu')"
          @click="mobileOpen = true"
        >
          <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
          </svg>
        </button>

        <!-- App name (mobile only — desktop sidebar shows it) -->
        <span class="lg:hidden text-white font-bold text-sm tracking-tight">Startline Admin</span>

        <div class="flex-1" />

        <span v-if="superAdmin" class="hidden sm:block text-slate-400 text-sm">{{ superAdmin.name }}</span>
        <button
          type="button"
          class="text-slate-400 hover:text-white text-sm font-medium transition-colors"
          @click="handleLogout"
        >
          {{ t('admin.auth.logout') }}
        </button>
      </header>

      <!-- Main scroll container — sticky elements inside scroll relative to this -->
      <main class="flex-1 overflow-y-auto px-6 py-8">
        <slot />
      </main>
    </div>
  </div>
</template>
